import { Link, useNavigate } from 'react-router-dom';
import { Bell, Check, Globe, LogOut, Menu, Moon, Search, Settings, Sun, User } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback, Popover, PopoverContent, PopoverTrigger, Separator } from '@/components/ui/misc';
import { EnumBadge } from '@/components/common/enum-badge';
import { OfflineIndicator } from '@/components/offline/offline-indicator';
import { useDashboardSummary, useMarkNotificationsRead, useNotifications } from '@/lib/api/domains';
import { useAuth } from '@/lib/auth';
import { LOCALES, useI18n } from '@/lib/i18n';
import { usePreferences, type ThemeMode } from '@/lib/preferences';
import { useOfficeToday } from '@/lib/today';
import { entityRoute } from '@/lib/registry/navigation';
import { cn, initials } from '@/lib/utils';
import { Kbd } from './command-palette';

const LOCALE_NAMES: Record<string, string> = { el: 'Ελληνικά', en: 'English' };

/**
 * The header's context line.
 *
 * Deliberately not cards, not tiles, not colour: a row of "N label" pairs where the
 * number is the only thing set in foreground ink. It answers "what is waiting for me"
 * without occupying a single row of the page below, and each pair is a link to the
 * screen that resolves it — so it is a shortcut, not decoration.
 *
 * Zero counts are dropped rather than shown as "0": an empty queue is not information
 * worth a slot in the chrome, and a strip that shrinks as work is cleared is quietly
 * satisfying in a way a row of zeroes is not.
 */
function ContextStrip({
  summary,
}: {
  summary: { upcomingRenewals: number; openClaims: number; unpaidPolicies: number } | undefined;
}) {
  const { t, number, date } = useI18n();
  /*
   * Spelled out rather than left implicit. The strip is the one line that claims to be about
   * today, and half of what it points at — a renewals board, an overdue list — is only true
   * relative to a date. On a machine left open across midnight «Σήμερα» was the only word on
   * screen that could be silently wrong, with no way to notice. Now it says which day it means,
   * and the hook makes sure it stops being yesterday at midnight in Athens rather than at
   * 02:00 or 03:00, which is when UTC turns over here.
   */
  const todayIso = useOfficeToday();

  if (!summary) return null;

  const entries = [
    { key: 'renewals', to: '/renewals', count: summary.upcomingRenewals, label: t('topbar.renewals') },
    { key: 'claims', to: '/claims?status=open', count: summary.openClaims, label: t('topbar.claims') },
    /*
     * Points at the filtered policy list, not at /collections. Collections left the navigation
     * when it was replaced by Ειδοποιητήρια, and this link was still sending anyone who clicked
     * the count to a screen no longer in the menu — and one that lists instalments rather than
     * the policies being counted.
     */
    {
      key: 'unpaid',
      to: '/policies?paymentStatus=unpaid,partially_paid',
      count: summary.unpaidPolicies,
      label: t('topbar.unpaid'),
    },
  ].filter((entry) => entry.count > 0);

  return (
    <div className="hidden min-w-0 items-center gap-3 xl:flex">
      <Separator orientation="vertical" className="bg-border h-5" />

      <span className="text-muted-soft shrink-0 text-[13px]">
        {t('topbar.today')} <span className="tabular">{date(todayIso)}</span>
      </span>

      {entries.length === 0 ? (
        <span className="text-muted-soft text-[13px]">{t('topbar.allClear')}</span>
      ) : (
        <div className="flex min-w-0 items-center gap-1">
          {entries.map((entry, index) => (
            <span key={entry.key} className="flex min-w-0 items-center">
              {index > 0 && <span className="text-muted-soft/50 px-1.5 text-[13px]">·</span>}
              <Link
                to={entry.to}
                className="hover:bg-accent -mx-1 flex items-center gap-1.5 rounded-md px-1.5 py-1 transition-colors duration-150"
              >
                <span className="tabular text-[13px] font-semibold">{number(entry.count)}</span>
                <span className="text-muted-foreground truncate text-[13px]">{entry.label}</span>
              </Link>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

export function Topbar({
  onOpenSearch,
  onOpenMobileNav,
}: {
  onOpenSearch: () => void;
  onOpenMobileNav: () => void;
}) {
  const { t, locale, setLocale, dateTime } = useI18n();
  const { user, signOut } = useAuth();
  const { theme, setTheme, resolvedTheme } = usePreferences();
  const navigate = useNavigate();

  const { data: notifications } = useNotifications();
  const markRead = useMarkNotificationsRead();
  const unread = notifications?.filter((n) => !n.readAt) ?? [];

  // The header's context line. An unfiltered summary, so it is a different cache entry
  // from the dashboard's filtered one and stays correct while a report is being sliced.
  const { data: summary } = useDashboardSummary({});

  return (
    <header className="bg-background/80 supports-[backdrop-filter]:bg-background/60 border-border sticky top-0 z-30 flex h-14 items-center gap-3 border-b px-3 backdrop-blur-xl print:hidden sm:px-4">
      <Button variant="ghost" size="icon-sm" className="lg:hidden" onClick={onOpenMobileNav} aria-label="Menu">
        <Menu />
      </Button>

      {/*
        Search is the primary navigation element, so it gets the shape of a field and
        the width of one — but it is a <button>, because it opens a palette. Rendering a
        real <input> here would promise that typing lands in the header when it actually
        lands in a dialog.

        `min-w-0 flex-1` below `sm`, not `w-full`. `w-full` asked for 100% of the header
        while five action buttons still needed room to the right of it, and since the action
        group could shrink and this could not, the group was pushed 17px past the viewport —
        clipping the user avatar on every single screen. Flexing instead lets search absorb
        whatever is left after the fixed chrome, and truncation inside it does the rest.
      */}
      <button
        type="button"
        onClick={onOpenSearch}
        className={cn(
          'group border-border/80 bg-card/60 text-muted-soft flex h-9 min-w-0 flex-1 items-center gap-2.5 rounded-lg border px-3 text-[13px]',
          'transition-[background-color,border-color,box-shadow] duration-150',
          'hover:border-border-strong hover:bg-card hover:shadow-xs',
          'focus-visible:border-ring focus-visible:ring-ring/35 focus-visible:ring-[3px] focus-visible:outline-none',
          'sm:w-72 sm:flex-none lg:w-80',
        )}
      >
        <Search className="size-4 shrink-0 opacity-70 transition-opacity duration-150 group-hover:opacity-100" />
        <span className="truncate">{t('search.short')}</span>
        <span className="ml-auto hidden shrink-0 items-center gap-1 sm:flex">
          <Kbd>⌘</Kbd>
          <Kbd>K</Kbd>
        </span>
      </button>

      <ContextStrip summary={summary} />

      {/* shrink-0: this group is fixed chrome and must never be the thing that gives way. */}
      <div className="ml-auto flex shrink-0 items-center gap-0.5">
        <OfflineIndicator />

        <Popover>
          <PopoverTrigger asChild>
            <Button variant="ghost" size="icon-sm" className="relative" aria-label={t('notification.title')}>
              <Bell />
              {unread.length > 0 && (
                <span className="bg-critical text-primary-foreground absolute -top-0.5 -right-0.5 grid min-w-4 place-items-center rounded-full px-1 text-[10px] font-semibold">
                  {unread.length > 9 ? '9+' : unread.length}
                </span>
              )}
            </Button>
          </PopoverTrigger>
          {/* w-96 and a wrapping-proof header: at w-80 the Greek "Σήμανση όλων ως
              αναγνωσμένων" was wider than the space left beside the title and spilled
              outside the panel, over the page behind it. */}
          <PopoverContent align="end" className="w-96 max-w-[calc(100vw-1.5rem)] overflow-hidden p-0">
            <div className="border-border flex items-center justify-between gap-2 border-b px-3 py-2">
              <p className="shrink-0 text-sm font-semibold">{t('notification.title')}</p>
              {unread.length > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="min-w-0 shrink"
                  onClick={() => markRead.mutate([])}
                >
                  <Check />
                  <span className="truncate">{t('notification.markAllRead')}</span>
                </Button>
              )}
            </div>

            <div className="max-h-80 overflow-y-auto">
              {!notifications?.length ? (
                <p className="text-muted-foreground py-10 text-center text-sm">{t('notification.empty')}</p>
              ) : (
                notifications.map((notification) => (
                  <button
                    key={notification.id}
                    type="button"
                    onClick={() => {
                      markRead.mutate([notification.id]);
                      if (notification.entityType && notification.entityId) {
                        navigate(entityRoute(notification.entityType, notification.entityId));
                      }
                    }}
                    className={cn(
                      'hover:bg-accent flex w-full flex-col items-start gap-0.5 border-b px-3 py-2.5 text-left last:border-b-0',
                      !notification.readAt && 'bg-primary/[0.045]',
                    )}
                  >
                    <span className="flex w-full items-start gap-2">
                      {!notification.readAt && (
                        <span className="bg-primary mt-1.5 size-1.5 shrink-0 rounded-full" aria-hidden />
                      )}
                      <span className="flex-1 text-[13px] font-medium">{notification.title}</span>
                    </span>
                    {notification.body && (
                      <span className="text-muted-foreground pl-3.5 text-xs">{notification.body}</span>
                    )}
                    <span className="text-muted-foreground/70 pl-3.5 text-[11px]">
                      {dateTime(notification.createdAt)}
                    </span>
                  </button>
                ))
              )}
            </div>
          </PopoverContent>
        </Popover>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon-sm" aria-label={t('settings.language')}>
              <Globe />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>{t('settings.language')}</DropdownMenuLabel>
            <DropdownMenuRadioGroup value={locale} onValueChange={(value) => setLocale(value as 'el')}>
              {LOCALES.map((code) => (
                <DropdownMenuRadioItem key={code} value={code}>
                  {LOCALE_NAMES[code] ?? code}
                </DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
          </DropdownMenuContent>
        </DropdownMenu>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon-sm" aria-label={t('settings.theme')}>
              {resolvedTheme === 'dark' ? <Moon /> : <Sun />}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>{t('settings.theme')}</DropdownMenuLabel>
            <DropdownMenuRadioGroup value={theme} onValueChange={(value) => setTheme(value as ThemeMode)}>
              <DropdownMenuRadioItem value="light">{t('settings.themeLight')}</DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="dark">{t('settings.themeDark')}</DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="system">{t('settings.themeSystem')}</DropdownMenuRadioItem>
            </DropdownMenuRadioGroup>
          </DropdownMenuContent>
        </DropdownMenu>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon-sm" className="ml-1 rounded-full" aria-label={t('auth.profile')}>
              <Avatar className="size-7">
                <AvatarFallback>{initials(user?.fullName ?? '')}</AvatarFallback>
              </Avatar>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-60">
            <div className="flex items-center gap-2.5 px-2 py-2">
              <Avatar className="size-9">
                <AvatarFallback>{initials(user?.fullName ?? '')}</AvatarFallback>
              </Avatar>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">{user?.fullName}</p>
                <p className="text-muted-foreground truncate text-xs">{user?.email}</p>
              </div>
            </div>
            <div className="px-2 pb-2">
              <EnumBadge enum="role" value={user?.role} size="sm" />
            </div>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => navigate('/settings')}>
              <Settings /> {t('nav.settings')}
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => navigate('/settings#account')}>
              <User /> {t('auth.profile')}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem variant="destructive" onSelect={() => void signOut()}>
              <LogOut /> {t('auth.signOut')}
              <DropdownMenuShortcut />
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}

export { Badge };
